import React, { useState, useEffect, useRef, useCallback } from 'react';
import { createPortal } from 'react-dom';
import PropTypes from 'prop-types';

import {
	Modal,
	Button,
	Table,
	Typography,
	Empty,
	Tag,
	Space,
	Spin,
} from 'antd';
import { DownOutlined, RightOutlined } from '@ant-design/icons';

import englan from '../../english.json';
import germanlan from '../../german.json';
import arabiclan from '../../arabic.json';
import frenchlan from '../../french.json';
import hindilan from '../../hindi.json';
import malayalamlan from '../../malayalam.json';

import { datetimeCovertertolocal } from '../datetime-converter';

const { Text } = Typography;

const dashboardtype = process.env.REACT_APP_IDENTIFIER;
const langid = localStorage.getItem(
	`${dashboardtype.toUpperCase()}_LANGUAGE_ID`
)
	? parseInt(
		localStorage.getItem(`${dashboardtype.toUpperCase()}_LANGUAGE_ID`),
		10
	)
	: 2;

const langdata =
    langid === 1? arabiclan: langid === 2? englan: langid === 3? frenchlan: langid === 4? germanlan: langid === 5? hindilan: langid === 6? malayalamlan : englan;

// ============================================================
// HELPERS
// ============================================================

// Render any single value as readable text.
const formatValue = (value) => {
	if (value === null || value === undefined || value === '') {
		return 'No Data';
	}

	if (typeof value === 'boolean') {
		return value ? 'true' : 'false';
	}

	if (typeof value === 'object') {
		return JSON.stringify(value);
	}

	return String(value);
};

// Format distance range values.
const formatDistanceRangeValue = (item) => {
	if (item === null || item === undefined || item === '') {
		return 'No Data';
	}

	const numberValue = Number(item);

	if (Number.isFinite(numberValue)) {
		return numberValue === 0
			? '0'
			: numberValue.toFixed(2);
	}

	return String(item);
};

// Column widths (px).
const INDEX_COLUMN_WIDTH = 75;
const ARRAY_COLUMN_WIDTH = 180;
const PEAK_FREQUENCY_COLUMN_WIDTH = 220;

// Channel frequency (nested) table settings.
// ~6 rows are visible, anything beyond that scrolls vertically.
const CHANNEL_TABLE_SCROLL_Y = 280;
const CHANNEL_EVENT_TYPE_WIDTH = 140;

// After the expanded table is attached to the page, the loader is hidden
// this many ms later (gives the browser time to paint the table).
const LOADER_HIDE_DELAY = 150;

// Safety net: the loader never stays longer than this (ms),
// even if something unexpected happens.
const LOADER_MAX_DURATION = 15000;

// Convert one ChannelFrequencies_Hz entry
// { 309: [5.2, 5.4, ...], 319: [...] }
// into rows for the nested table.
const buildChannelRows = (channelObj) => {
	if (!channelObj || typeof channelObj !== 'object') {
		return [];
	}

	return Object.entries(channelObj).map(([eventType, frequencies]) => ({
		key: eventType,
		eventType,
		frequencies: Array.isArray(frequencies) ? frequencies : [],
	}));
};

// ============================================================
// COMPONENTS
// ============================================================

// Display the label and value on the same line.
// Supports custom rendering for values such as distanceRange.
const SingleValueRow = ({ label, value, render }) => (
	<div
		style={{
			display: 'flex',
			alignItems: 'flex-start',
			gap: 8,
			marginBottom: 12,
			width: '100%',
		}}
	>
		<Text strong style={{ flexShrink: 0 }}>
			{label}:
		</Text>

		<div
			style={{
				minWidth: 0,
				overflowWrap: 'anywhere',
				wordBreak: 'break-word',
			}}
		>
			{render ? render(value) : formatValue(value)}
		</div>
	</div>
);

SingleValueRow.propTypes = {
	label: PropTypes.string.isRequired,
	value: PropTypes.any,
	render: PropTypes.func,
};

// Full screen loading overlay (nested table expansion).
// Rendered into document.body so it covers the whole screen, above the modal.
const FullScreenLoader = () =>
	createPortal(
		<div
			style={{
				position: 'fixed',
				top: 0,
				left: 0,
				right: 0,
				bottom: 0,
				zIndex: 2000,
				display: 'flex',
				alignItems: 'center',
				justifyContent: 'center',
			}}
		>
			<Spin />
		</div>,
		document.body
	);

// Nested table: Event Type | Frequency (tags in one horizontal line,
// one horizontal scroll for the whole table).
const ChannelFrequencyTable = ({ rows }) => {
	const channelColumns = [
		{
			title:
                langdata && langdata.EventPositions ? langdata.EventPositions : 'Event Positions',
			dataIndex: 'eventType',
			key: 'eventType',
			width: CHANNEL_EVENT_TYPE_WIDTH,
			align: 'center',
		},
		{
			title:
                langdata && langdata.Frequency ? `${langdata.Frequency} (Hz)` : 'Frequency (Hz)',
			dataIndex: 'frequencies',
			key: 'frequencies',
			render: (frequencies) =>
				Array.isArray(frequencies) && frequencies.length > 0 ? (
					<div style={{ display: 'flex', flexWrap: 'nowrap', gap: 6 }}>
						{frequencies.map((freq, index) => (
							<Tag
								color="blue"
								key={index}
								style={{ marginInlineEnd: 0, flexShrink: 0 }}
							>
								{formatValue(freq)}
							</Tag>
						))}
					</div>
				) : (
					<Tag color="default" style={{ marginInlineEnd: 0 }}>
                        No Data
					</Tag>
				),
		},
	];

	return (
		<Table
			size="small"
			bordered
			columns={channelColumns}
			dataSource={rows}
			pagination={false}
			scroll={{ x: 'max-content', y: CHANNEL_TABLE_SCROLL_Y }}
		/>
	);
};

ChannelFrequencyTable.propTypes = {
	rows: PropTypes.arrayOf(
		PropTypes.shape({
			key: PropTypes.string,
			eventType: PropTypes.string,
			frequencies: PropTypes.array,
		})
	).isRequired,
};

// Peak Frequency cell: value on the left, expand icon at the right end.
const PeakFrequencyCell = ({
	value,
	hasChannelData,
	isExpanded,
	onToggle,
}) => (
	<div
		style={{
			display: 'flex',
			alignItems: 'center',
			justifyContent: 'space-between',
			gap: 8,
		}}
	>
		<span>{formatValue(value)}</span>

		{hasChannelData && (
			<Button
				type="text"
				size="small"
				aria-label="Toggle channel frequencies"
				onClick={onToggle}
				icon={isExpanded ? <DownOutlined /> : <RightOutlined />}
			/>
		)}
	</div>
);

PeakFrequencyCell.propTypes = {
	value: PropTypes.oneOfType([
		PropTypes.string,
		PropTypes.number,
		PropTypes.bool,
	]),
	hasChannelData: PropTypes.bool.isRequired,
	isExpanded: PropTypes.bool.isRequired,
	onToggle: PropTypes.func.isRequired,
};

// ============================================================
// MAIN COMPONENT
//
// The data is NOT mapped here anymore. RealtimeDetection calls the API when
// the info icon is clicked, the reducer extracts the exact fields below, and
// they arrive here through the `modalData` prop (`modalLoading` drives the
// antd spinner).
// ============================================================

export default function DetectionInfoModal({
	open,
	record,
	modalData,
	modalLoading,
	onClose,
	trajectoryBoolean,
}) {
	// Keys of the rows whose channel frequency table is expanded.
	const [expandedRowKeys, setExpandedRowKeys] = useState([]);

	// Full screen loader visibility (nested table expansion).
	const [isTableLoading, setIsTableLoading] = useState(false);

	// The row key that is currently being opened (waiting for its table to render).
	const [pendingKey, setPendingKey] = useState(null);

	// Timer used to hide the loader shortly after the table is on the page.
	const hideTimerRef = useRef(null);

	// ------------------------------------------------------------
	// Loader helpers
	// ------------------------------------------------------------

	const clearHideTimer = useCallback(() => {
		if (hideTimerRef.current) {
			clearTimeout(hideTimerRef.current);
			hideTimerRef.current = null;
		}
	}, []);

	const finishLoading = useCallback(() => {
		clearHideTimer();
		setIsTableLoading(false);
		setPendingKey(null);
	}, [clearHideTimer]);

	// Hide the loader a moment from now. Safe to call many times:
	// only one timer is ever active.
	const scheduleFinishLoading = useCallback(() => {
		if (hideTimerRef.current) {
			return;
		}

		hideTimerRef.current = setTimeout(() => {
			hideTimerRef.current = null;
			setIsTableLoading(false);
			setPendingKey(null);
		}, LOADER_HIDE_DELAY);
	}, []);

	// Reference callback for the expanded content.
	// React calls it with the DOM node the moment the expanded table is
	// attached to the page, which is exactly when the loader can go away.
	const expandedContentRef = useCallback(
		(node) => {
			if (node) {
				scheduleFinishLoading();
			}
		},
		[scheduleFinishLoading]
	);

	// ------------------------------------------------------------
	// Effects
	// ------------------------------------------------------------

	// Collapse everything whenever the modal is opened/closed, the record
	// changes, or fresh API data arrives.
	useEffect(() => {
		clearHideTimer();
		setExpandedRowKeys([]);
		setIsTableLoading(false);
		setPendingKey(null);
	}, [open, record, modalData, trajectoryBoolean, clearHideTimer]);

	// Backup trigger: as soon as the row being opened is in the expanded list,
	// start the hide timer (in case the reference callback did not fire).
	useEffect(() => {
		if (pendingKey !== null && expandedRowKeys.includes(pendingKey)) {
			scheduleFinishLoading();
		}
	}, [expandedRowKeys, pendingKey, scheduleFinishLoading]);

	// Safety net: never leave the loader on forever.
	useEffect(() => {
		if (!isTableLoading) {
			return undefined;
		}

		const safetyTimer = setTimeout(finishLoading, LOADER_MAX_DURATION);

		return () => clearTimeout(safetyTimer);
	}, [isTableLoading, finishLoading]);

	// Clear the timer when the component unmounts.
	useEffect(() => clearHideTimer, [clearHideTimer]);

	// ------------------------------------------------------------
	// Expand / collapse
	// ------------------------------------------------------------

	const toggleRow = (key) => {
		// Collapse immediately, no loader needed.
		if (expandedRowKeys.includes(key)) {
			setExpandedRowKeys((prev) => prev.filter((k) => k !== key));
			return;
		}

		// Show the full screen loader first.
		clearHideTimer();
		setPendingKey(key);
		setIsTableLoading(true);

		// Wait for the loader to paint, then render the heavy table.
		requestAnimationFrame(() => {
			setTimeout(() => {
				setExpandedRowKeys((prev) => [...prev, key]);
			}, 0);
		});
	};

	const title =
        record?.detectionAlgorithmTypeName ||
        (langdata && langdata.DetectionAlgorithm? langdata.DetectionAlgorithm: 'Detection Info'
        );

	// ============================================================
	// DATA (already extracted by the reducer from the API response)
	// ============================================================

	const data = modalData || {};

	// ============================================================
	// SINGLE VALUES
	// Display one value per line.
	// ============================================================

	const singleItems = [
		{
			id: 'cpaSourceDatetime',
			label:
                langdata && langdata.CPASourceDatetime ? langdata.CPASourceDatetime : 'CPA Source Datetime',
			value: data.cpaSourceDatetime,
		},
		{
			id: 'cpaSourceDistance',
			label:
                langdata && langdata.CPASourceDistance ? langdata.CPASourceDistance : 'CPA Source Distance',
			value: data.cpaSourceDistance,
		},
		{
			id: 'distanceRange',
			label: langdata?.distanceRange
				? `${langdata.distanceRange} (m)`
				: 'Distance Range (m)',
			value: data.distanceRange,
			render: (value) => (
				<Space wrap size={[8, 8]}>
					{Array.isArray(value) && value.length > 0 ? (
						value.map((item, index) => (
							<Tag
								color="blue"
								key={index}
								style={{
									marginInlineEnd: 0,
								}}
							>
								{formatDistanceRangeValue(item)}
							</Tag>
						))
					) : (
						<Tag
							color="gold"
							style={{
								marginInlineEnd: 0,
							}}
						>
                            No Data
						</Tag>
					)}
				</Space>
			),
		},
		{
			id: 'depthRange',
			label: langdata?.depthRange
				? `${langdata.depthRange} (m)`
				: 'Depth Range (m)',
			value: data.depthRange,
			render: (value) => (
				<Space wrap size={[8, 8]}>
					{Array.isArray(value) && value.length > 0 ? (
						value.map((item, index) => (
							<Tag
								color="purple"
								key={index}
								style={{
									marginInlineEnd: 0,
								}}
							>
								{formatDistanceRangeValue(item)}
							</Tag>
						))
					) : (
						<Tag
							color="gold"
							style={{
								marginInlineEnd: 0,
							}}
						>
                            No Data
						</Tag>
					)}
				</Space>
			),
		},
	].filter(
		(item) =>
			item.value !== null &&
            item.value !== undefined
	);

	// ============================================================
	// ARRAY VALUES
	// These become the table columns.
	// Only display arrays that contain data.
	// ============================================================

	const arrayItems = [
		{
			id: 'peakFrequency_Hz',
			label:
                langdata && langdata.PeakFrequencyHz? langdata.PeakFrequencyHz: 'Peak Frequency (Hz)',
			values: data.peakFrequency_Hz,
		},
		{
			id: 'ber',
			label:
                langdata && langdata.BER? langdata.BER: 'BER',
			values: data.ber,
		},
		{
			id: 'pmr',
			label:
                langdata && langdata.PMR? langdata.PMR: 'PMR',
			values: data.pmr,
		},
		{
			id: 'dominantBand',
			label:
                langdata && langdata.DominantBand? langdata.DominantBand: 'Dominant Band',
			values: data.dominantBand,
		},
		{
			id: 'spectralFlatness',
			label:
                langdata && langdata.SpectralFlatness? langdata.SpectralFlatness: 'Spectral Flatness',
			values: data.spectralFlatness,
		},
		{
			id: 'starboardLatitude',
			label:
                langdata && langdata.StarboardLatitude? langdata.StarboardLatitude: 'Starboard Latitude',
			values: data.starboardLatitude,
		},
		{
			id: 'starboardLongitude',
			label:
                langdata && langdata.StarboardLongitude? langdata.StarboardLongitude: 'Starboard Longitude',
			values: data.starboardLongitude,
		},
		{
			id: 'eventPositions',
			label:
                langdata && langdata.EventPositions? langdata.EventPositions: 'Event Positions',
			values: data.eventPositions,
		},
		{
			id: 'sourceDistances',
			label:
                langdata && langdata.SourceDistances? `${langdata.SourceDistances} (m)`: 'Source Distances (m)',
			values: data.sourceDistances,
		},
		{
			id: 'sourceSpeedKts',
			label:
                langdata && langdata.SourceSpeedKts? langdata.SourceSpeedKts: 'Source Speed (Kts)',
			values: data.sourceSpeedKts,
		},
		{
			id: 'sourceSpeedms',
			label:
                langdata && langdata.SourceSpeedms? langdata.SourceSpeedms: 'Source Speed (m/s)',
			values: data.sourceSpeedms,
		},
		{
			id: 'timeStart',
			label:
				langdata && langdata.timeStart ? langdata.timeStart : 'Time Start',
			values: (data.timeStart || []).map((startTime) => {
				if (!startTime) {
					return null;
				}

				const datetime = datetimeCovertertolocal(startTime);

				return `${datetime.localdate} ${datetime.localtime}`;
			}),
		},
	].filter(
		(item) => item.values && item.values.length > 0
	);

	const hasAnyData =
        singleItems.length > 0 ||
        arrayItems.length > 0;

	// ============================================================
	// TABLE DATA
	// One row per index and one column per array key.
	// Row count follows the longest array.
	// Shorter arrays display 'No Data'.
	// ============================================================

	const rowCount = Math.max(
		0,
		...arrayItems.map((item) => item.values.length)
	);

	const tableData = [];

	for (let i = 0; i < rowCount; i += 1) {
		const row = {
			key: i,
			index: i + 1,
			// Rows for the nested Event Type / Frequency table.
			channelRows: buildChannelRows(data.ChannelFrequencies_Hz?.[i]),
		};

		arrayItems.forEach((item) => {
			row[item.id] = item.values[i];
		});

		tableData.push(row);
	}

	// ============================================================
	// TABLE COLUMNS
	// ============================================================

	const columns = [
		{
			title: 'Sl No.',
			dataIndex: 'index',
			key: 'index',
			width: INDEX_COLUMN_WIDTH,
			align: 'center',
			fixed: 'left',
		},
		...arrayItems.map((item) => {
			// Peak Frequency: value on the left, expand icon at the right end.
			if (item.id === 'peakFrequency_Hz') {
				return {
					title: item.label,
					dataIndex: item.id,
					key: item.id,
					width: PEAK_FREQUENCY_COLUMN_WIDTH,
					render: (value, row) => (
						<PeakFrequencyCell
							value={value}
							hasChannelData={Boolean(row.channelRows?.length)}
							isExpanded={expandedRowKeys.includes(row.key)}
							onToggle={() => toggleRow(row.key)}
						/>
					),
				};
			}

			return {
				title: item.label,
				dataIndex: item.id,
				key: item.id,
				width: ARRAY_COLUMN_WIDTH,
				render: (value) => formatValue(value),
			};
		}),
	];

	// Total table width based on the configured column widths.
	const tableScrollX =
        INDEX_COLUMN_WIDTH +
        arrayItems.reduce((sum, item) => sum + (item.id === 'peakFrequency_Hz'? PEAK_FREQUENCY_COLUMN_WIDTH: ARRAY_COLUMN_WIDTH),0
        );

	// ============================================================
	// RENDER
	// ============================================================

	return (
		<>
			<Modal
				title={
					<div style={{ textAlign: 'center' }}>
						{title}
					</div>
				}
				open={open}
				onCancel={onClose}
				centered
				width="76vw"
				styles={{
					content: {
						maxWidth: '95vw',
					},
					header: {
						marginBottom: 24,
					},
					body: {},
				}}
				destroyOnClose
				footer={[
					<Button
						key="close"
						onClick={onClose}
					>
						{langdata && langdata.CLOSE
							? langdata.CLOSE
							: 'CLOSE'}
					</Button>,
				]}
			>
				{/* antd Spin: ON while the API is running, OFF once the data is in the reducer */}
				<Spin spinning={Boolean(modalLoading)}>
					<div
						style={{
							width: '100%',
							minWidth: 0,
							minHeight: 150,
						}}
					>
						{modalLoading ? null : !modalData || !hasAnyData ? (
							<Empty
								image={Empty.PRESENTED_IMAGE_SIMPLE}
							/>
						) : (
							<>
								{/* Single values: one value per line */}

								{singleItems.length > 0 && (
									<div
										style={{
											width: '100%',
										}}
									>
										{singleItems.map((item) => (
											<SingleValueRow
												key={item.id}
												label={item.label}
												value={item.value}
												render={item.render}
											/>
										))}
									</div>
								)}

								{/* Array values displayed in the table */}

								{arrayItems.length > 0 && (
									<Table
										style={{
											width: '100%',
										}}
										scroll={{
											x: tableScrollX,
										}}
										columns={columns}
										dataSource={tableData}
										pagination={
											tableData.length > 10
												? {
													pageSize: 10,
													showSizeChanger: false,
												}
												: false
										}
										expandable={{
											// Expansion is controlled by the icon inside the
											// Peak Frequency cell, so hide antd's own icon column.
											showExpandColumn: false,
											expandedRowKeys,
											rowExpandable: (row) =>
												row.channelRows && row.channelRows.length > 0,
											expandedRowRender: (row) => (
												// sticky keeps the nested table inside the visible
												// modal area even when the parent scrolls horizontally.
												// The ref tells us the moment the table is on the page.
												<div
													ref={expandedContentRef}
													style={{
														position: 'sticky',
														left: 0,
														width: 'calc(76vw - 120px)',
														maxWidth: '100%',
													}}
												>
													<ChannelFrequencyTable rows={row.channelRows} />
												</div>
											),
										}}
									/>
								)}
							</>
						)}
					</div>
				</Spin>
			</Modal>

			{/* Full screen loader: visible from the arrow click until the nested table is on the page */}
			{isTableLoading && <FullScreenLoader />}
		</>
	);
}

// ============================================================
// PROP TYPES
// ============================================================

// A single array item inside the extracted data.
const arrayItemPropType = PropTypes.oneOfType([
	PropTypes.string,
	PropTypes.number,
	PropTypes.bool,
]);

DetectionInfoModal.propTypes = {
	open: PropTypes.bool,

	// Table row that was clicked — only used for the modal title.
	record: PropTypes.shape({
		id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
		detectionAlgorithmTypeName: PropTypes.string,
	}),

	// Extracted by the reducer from the API response (null until loaded).
	modalData: PropTypes.shape({
		cpaSourceDatetime: PropTypes.string,
		cpaSourceDistance: PropTypes.oneOfType([
			PropTypes.string,
			PropTypes.number,
		]),
		distanceRange: PropTypes.arrayOf(arrayItemPropType),
		ber: PropTypes.arrayOf(arrayItemPropType),
		pmr: PropTypes.arrayOf(arrayItemPropType),
		dominantBand: PropTypes.arrayOf(arrayItemPropType),
		peakFrequency_Hz: PropTypes.arrayOf(arrayItemPropType),
		spectralFlatness: PropTypes.arrayOf(arrayItemPropType),
		starboardLatitude: PropTypes.arrayOf(arrayItemPropType),
		starboardLongitude: PropTypes.arrayOf(arrayItemPropType),
		eventPositions: PropTypes.arrayOf(arrayItemPropType),
		sourceDistances: PropTypes.arrayOf(arrayItemPropType),
		sourceSpeedKts: PropTypes.arrayOf(arrayItemPropType),
		sourceSpeedms: PropTypes.arrayOf(arrayItemPropType),
		timeStart: PropTypes.arrayOf(arrayItemPropType),
		// [{ eventType: [frequencies...] }, ...]
		ChannelFrequencies_Hz: PropTypes.arrayOf(
			PropTypes.objectOf(PropTypes.arrayOf(PropTypes.number))
		),
	}),

	// true while the API is running -> antd Spin.
	modalLoading: PropTypes.bool,

	onClose: PropTypes.func.isRequired,

	trajectoryBoolean: PropTypes.bool,
};

DetectionInfoModal.defaultProps = {
	open: false,
	record: null,
	modalData: null,
	modalLoading: false,
	trajectoryBoolean: false,
};